import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import moment from 'moment';
import {
    Percent,
    Info,
    Calculator,
    RefreshCw,
    Wallet,
    PiggyBank,
    CalendarClock,
} from 'lucide-react';

import { brandSelectTheme } from '../Common/reactSelectTheme';
import * as TaxPlanningService from '../../../services/TaxPlanningService';
import * as YearEndService from '../../../services/YearEndService';
import { formatSek, fmtPct } from '../YearEnd/constants';

/** Parse a Swedish-typed amount ("12 500,50") into a number. */
function parseAmount(raw) {
    if (raw == null) return NaN;
    const cleaned = String(raw).replace(/[\s ]/g, '').replace(',', '.');
    if (cleaned === '') return NaN;
    return Number(cleaned);
}

function parseOptionalAmount(raw) {
    if (String(raw ?? '').trim() === '') return 0;
    return parseAmount(raw);
}

/** Local help copy for the one input the backend has no helpTexts key for. */
const OWN_SALARY_HELP = {
    label: 'Egen kontant lön året före',
    help: 'Din egen kontanta bruttolön (inte förmåner) som bolaget betalade ut under '
        + 'året före beskattningsåret. Används för taket på det lönebaserade utrymmet '
        + '(50 × egen eller närståendes lön).',
};

/**
 * Info-icon popover fed by the backend's helpTexts — one source of truth
 * with the agent interview, rendered verbatim.
 */
const InfoIcon = ({ id, entry }) => {
    if (!entry?.help) return null;
    return (
        <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="top"
            overlay={(
                <Popover id={id} style={{ maxWidth: 340 }}>
                    <Popover.Header as="h3" style={{ fontSize: '0.8125rem' }}>
                        {entry.label}
                    </Popover.Header>
                    <Popover.Body style={{ fontSize: '0.8125rem', lineHeight: 1.55 }}>
                        {entry.help}
                    </Popover.Body>
                </Popover>
            )}
        >
            <button type="button" className="tp-info-btn" aria-label={`Hjälp: ${entry.label}`}>
                <Info size={14} />
            </button>
        </OverlayTrigger>
    );
};

/**
 * Skatteplanering — the what-if page (route /:companyId/tax-planning).
 * Three cards over the backend's tax-planning calculators: (1) Lön i år —
 * the December salary levers (gap to brytpunkten and PGI-optimum, hard
 * 31 Dec deadline per kontantprincipen); (2) Utdelningsutrymme (K10) —
 * gränsbelopp what-if with the dual-rule engine (old rules ≤ 2025, new
 * merged rule from 2026), every owner input explained by the backend's
 * shared helpTexts; (3) Periodiseringsfond — avsättning headroom,
 * schablonintäkt cost and forced reversals for a chosen fiscal year.
 * Admin-gated server-side (403 → info card). Nothing here books or files
 * anything — the disclaimer from the backend closes the page.
 */
function TaxPlanningPage() {
    const { companyId } = useParams();
    const currentYear = new Date().getFullYear();

    // Fiscal years (periodiseringsfond context)
    const [fiscalYears, setFiscalYears] = useState([]);
    const [selectedFyId, setSelectedFyId] = useState(null);
    const [fySeeded, setFySeeded] = useState(false);

    // Overview
    const [overview, setOverview] = useState(null);
    const [helpTexts, setHelpTexts] = useState({});
    const [loadingOverview, setLoadingOverview] = useState(true);
    const [overviewError, setOverviewError] = useState(null);
    const [forbidden, setForbidden] = useState(false);

    // Salary card — manual YTD input when the books can't tell
    const [ytdSalaryInput, setYtdSalaryInput] = useState('');
    const [ytdOverride, setYtdOverride] = useState(null);
    // Enskild firma: kapitalunderlaget för räntefördelning matas in av
    // användaren (det går inte att härleda ur bokföringen — SKV 2196).
    const [kapitalunderlag, setKapitalunderlag] = useState('');
    const [kapitalunderlagOverride, setKapitalunderlagOverride] = useState(null);
    // Expansionsfondens kapitalunderlag är ett ANNAT tal än räntefördelningens
    // (IL 34:7 mäter vid årets utgång, IL 33:8 vid föregående års utgång och
    // minskar dessutom med 79,4 % av fonden). Egen inmatning, egen state.
    const [efUnderlag, setEfUnderlag] = useState('');
    const [efUnderlagOverride, setEfUnderlagOverride] = useState(null);
    const [ingaendeEf, setIngaendeEf] = useState('');

    // K10 card
    const [beskattningsar, setBeskattningsar] = useState(currentYear);
    const [agarandel, setAgarandel] = useState('100');
    const [omkostnadsbelopp, setOmkostnadsbelopp] = useState('');
    const [sparat, setSparat] = useState('');
    const [egenLon, setEgenLon] = useState('');
    const [narstaendeLon, setNarstaendeLon] = useState('');
    const [k10Result, setK10Result] = useState(null);
    const [k10Error, setK10Error] = useState(null);
    const [computingK10, setComputingK10] = useState(false);

    // ---- Fiscal years -----------------------------------------------------

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await YearEndService.listFiscalYears(companyId);
                if (cancelled) return;
                const years = (data?.fiscalYears || [])
                    .slice()
                    .sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
                setFiscalYears(years);
                // Default: the most recently ENDED year — the bokslut context
                // where periodiseringsfond decisions actually happen.
                const today = moment();
                const ended = years.find((fy) => moment(fy.end_date).isBefore(today, 'day'));
                setSelectedFyId((ended || years[0])?.fiscal_year_id
                    ?? (ended || years[0])?.id ?? null);
            } catch (err) {
                // Non-fatal — the pfond card just stays hidden.
            } finally {
                if (!cancelled) setFySeeded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [companyId]);

    const yearOptions = useMemo(() => fiscalYears.map((fy) => ({
        value: fy.fiscal_year_id ?? fy.id,
        label: `${fy.year} (${moment(fy.start_date).format('YYYY-MM-DD')} – ${moment(fy.end_date).format('YYYY-MM-DD')})`,
    })), [fiscalYears]);

    // ---- Overview ---------------------------------------------------------

    const fetchOverview = useCallback(async ({
        ytdCashSalary, kapitalunderlag: kuArg,
        expansionsfondKapitalunderlag: efArg, ingaendeExpansionsfond: ingArg,
    } = {}) => {
        setLoadingOverview(true);
        setOverviewError(null);
        setForbidden(false);
        try {
            const body = { calendarYear: currentYear };
            if (selectedFyId) body.fiscalYearId = selectedFyId;
            // owner samlar BÅDA ägaruppgifterna — skriv aldrig över den ena
            // med den andra (kapitalunderlaget måste överleva en löneomräkning).
            const ku = kuArg != null ? kuArg : kapitalunderlagOverride;
            const owner = {};
            if (ytdCashSalary != null) owner.ytdCashSalary = ytdCashSalary;
            if (ku != null && Number.isFinite(ku)) owner.kapitalunderlag = ku;
            const ef = efArg != null ? efArg : efUnderlagOverride;
            if (ef != null && Number.isFinite(ef)) owner.expansionsfondKapitalunderlag = ef;
            const ing = ingArg != null ? ingArg : parseOptionalAmount(ingaendeEf);
            if (Number.isFinite(ing) && ing > 0) owner.ingaendeExpansionsfond = ing;
            if (Object.keys(owner).length > 0) body.owner = owner;
            const resp = await TaxPlanningService.getOverview(companyId, body);
            setOverview(resp?.data || null);
            if (resp?.helpTexts) setHelpTexts(resp.helpTexts);
        } catch (err) {
            if (err.response?.status === 403) {
                setForbidden(true);
            } else {
                setOverviewError(err.response?.data?.message
                    || 'Kunde inte hämta skatteplaneringen. Försök igen senare.');
            }
        } finally {
            setLoadingOverview(false);
        }
    }, [companyId, currentYear, selectedFyId, kapitalunderlagOverride,
        efUnderlagOverride, ingaendeEf]);

    useEffect(() => {
        if (!fySeeded) return;
        fetchOverview(ytdOverride != null ? { ytdCashSalary: ytdOverride } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fySeeded, fetchOverview]);

    const wages = overview?.wages || null;
    const salaryGaps = overview?.salaryGaps || null;
    const pfond = overview?.periodiseringsfond || null;
    const needsSalaryInput = Boolean(overview
        && (overview.needsUserInput || []).includes('owner_ytd_salary_or_employee'));

    // ---- K10 --------------------------------------------------------------

    const agarandelValue = parseAmount(agarandel);
    const k10InputsValid = Number.isFinite(agarandelValue)
        && agarandelValue > 0 && agarandelValue <= 100
        && Number.isFinite(parseOptionalAmount(omkostnadsbelopp))
        && Number.isFinite(parseOptionalAmount(sparat))
        && Number.isFinite(parseOptionalAmount(egenLon))
        && Number.isFinite(parseOptionalAmount(narstaendeLon));

    const invalidateK10 = () => { setK10Result(null); setK10Error(null); };

    const handleComputeK10 = async () => {
        setComputingK10(true);
        setK10Error(null);
        try {
            const resp = await TaxPlanningService.computeK10(companyId, {
                beskattningsar,
                ownershipShare: agarandelValue / 100,
                omkostnadsbelopp: parseOptionalAmount(omkostnadsbelopp),
                sparatUtdelningsutrymme: parseOptionalAmount(sparat),
                ownCashSalaryPrevYear: parseOptionalAmount(egenLon),
                narstaendeCashSalaryPrevYear: parseOptionalAmount(narstaendeLon),
            });
            setK10Result(resp?.data || null);
            if (resp?.helpTexts) setHelpTexts((prev) => ({ ...prev, ...resp.helpTexts }));
        } catch (err) {
            setK10Error(err.response?.data?.message
                || 'Kunde inte beräkna gränsbeloppet. Försök igen.');
        } finally {
            setComputingK10(false);
        }
    };

    // ---- Renderers: Lön i år ----------------------------------------------

    const renderSalaryBar = () => {
        const ytd = Number(salaryGaps?.ownerYtdCashSalary) || 0;
        const brytpunkt = Number(salaryGaps?.brytpunkt?.threshold) || 0;
        const pgi = Number(salaryGaps?.pgi?.threshold) || 0;
        const scale = Math.max(brytpunkt, pgi, ytd) * 1.08 || 1;
        const pct = (v) => Math.min(100, Math.max(0, (v / scale) * 100));
        const belowThresholds = ytd <= Math.min(brytpunkt, pgi) + 0.005;
        return (
            <div className="tp-bar-wrap">
                <div className="tp-bar">
                    <div
                        className={`tp-bar-fill ${belowThresholds ? '' : 'tp-bar-fill-over'}`}
                        style={{ width: `${pct(ytd)}%` }}
                    />
                    <div className="tp-bar-marker" style={{ left: `${pct(pgi)}%` }}>
                        <span className="tp-bar-marker-label">
                            PGI-tak {formatSek(pgi)}
                        </span>
                    </div>
                    <div className="tp-bar-marker tp-bar-marker-low" style={{ left: `${pct(brytpunkt)}%` }}>
                        <span className="tp-bar-marker-label tp-bar-marker-label-low">
                            Brytpunkt {formatSek(brytpunkt)}
                        </span>
                    </div>
                </div>
                <div className="tp-bar-caption">
                    Din kontanta lön hittills i år: <strong>{formatSek(ytd)}</strong>
                    {wages?.ownerSalarySource === 'konto_7220' && ' (från konto 7220, lön till företagsledare)'}
                    {wages?.ownerSalarySource === 'payroll_single_employee' && ' (från lönekörningarna)'}
                    {wages?.ownerSalarySource === 'explicit' && ' (angiven av dig)'}
                </div>
            </div>
        );
    };

    const renderSalaryCard = () => (
        <div className="modern-card">
            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">
                        <Wallet size={18} />
                        Lön i år
                    </h4>
                    <p className="section-subtitle">
                        Lön räknas det år den betalas ut (kontantprincipen) — de här
                        spakarna nollställs 31 december. Två nivåer är intressanta:
                        brytpunkten för statlig inkomstskatt och taket för allmän
                        pension (PGI).
                    </p>
                </div>
            </div>
            <div className="modern-card-body">
                {needsSalaryInput ? (
                    <>
                        <p className="text-muted" style={{ fontSize: '0.875rem', maxWidth: '62ch' }}>
                            Vi kunde inte utläsa ägarens lön ur bokföringen (flera anställda
                            utan markerad ägare). Ange din kontanta bruttolön hittills i år
                            så räknar vi ut avstånden.
                        </p>
                        <div className="ye-form-grid">
                            <div className="ye-field">
                                <label htmlFor="tp-ytd">Kontant bruttolön hittills i år (kr)</label>
                                <input
                                    id="tp-ytd"
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="t.ex. 420 000"
                                    value={ytdSalaryInput}
                                    onChange={(e) => setYtdSalaryInput(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="ye-proposal-actions">
                            <button
                                className="btn btn-primary d-inline-flex align-items-center gap-2"
                                onClick={() => {
                                    const v = parseOptionalAmount(ytdSalaryInput);
                                    if (!Number.isFinite(v) || v < 0) return;
                                    setYtdOverride(v);
                                    fetchOverview({ ytdCashSalary: v });
                                }}
                                disabled={!Number.isFinite(parseOptionalAmount(ytdSalaryInput))}
                            >
                                <Calculator size={16} />
                                Beräkna
                            </button>
                        </div>
                    </>
                ) : salaryGaps ? (
                    <>
                        {salaryGaps.unavailable ? (
                            <div className="ye-well" style={{ marginTop: 0 }}>{salaryGaps.unavailable}</div>
                        ) : (
                            <>
                                {renderSalaryBar()}
                                <div className="tp-tiles">
                                    <div className="tp-tile">
                                        <span className="tp-tile-label">Kvar till brytpunkten för statlig skatt</span>
                                        <span className="tp-tile-value">
                                            {Number(salaryGaps.brytpunkt?.gap) > 0
                                                ? formatSek(salaryGaps.brytpunkt.gap)
                                                : 'Passerad'}
                                        </span>
                                        {salaryGaps.brytpunkt?.note && (
                                            <span className="tp-tile-note">{salaryGaps.brytpunkt.note}</span>
                                        )}
                                    </div>
                                    <div className="tp-tile">
                                        <span className="tp-tile-label">Kvar till PGI-taket (allmän pension)</span>
                                        <span className="tp-tile-value">
                                            {Number(salaryGaps.pgi?.gap) > 0
                                                ? formatSek(salaryGaps.pgi.gap)
                                                : 'Passerat'}
                                        </span>
                                        {salaryGaps.pgi?.note && (
                                            <span className="tp-tile-note">{salaryGaps.pgi.note}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="ye-well">
                                    <CalendarClock size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                                    Sista dag: <strong>31 december</strong>.{' '}
                                    {salaryGaps.deadlineNote}
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        Ingen lönedata att visa.
                    </p>
                )}
            </div>
        </div>
    );

    // ---- Renderers: K10 ---------------------------------------------------

    const K10_COMPONENT_LABELS = {
        grundbelopp: 'Grundbelopp',
        lonebaserat: 'Lönebaserat utrymme',
        ranta: 'Ränta på omkostnadsbelopp',
        sparat: 'Sparat utdelningsutrymme',
        forenklingsregeln: 'Förenklingsregeln (schablon)',
    };

    const renderComponentRow = (key, comp) => {
        if (!comp || comp.amount == null) return null;
        return (
            <div className="tp-comp-row" key={key}>
                <div className="tp-comp-main">
                    <span>{K10_COMPONENT_LABELS[key] || key}</span>
                    <span className="ye-num">{formatSek(comp.amount)}</span>
                </div>
                {comp.formula && <div className="tp-comp-formula">{comp.formula}</div>}
                {comp.note && <div className="tp-comp-formula">{comp.note}</div>}
            </div>
        );
    };

    const renderK10Result = () => {
        if (!k10Result) return null;
        const comps = k10Result.components || {};
        const oldRules = k10Result.rules === 'old_pre2026';
        return (
            <div className="tp-k10-result">
                <div className="tp-tile tp-tile-primary">
                    <span className="tp-tile-label">
                        Gränsbelopp {k10Result.beskattningsar}
                        {oldRules && k10Result.chosenMethod && (
                            <span className="ye-badge ye-badge-primary" style={{ marginLeft: 8 }}>
                                {k10Result.chosenMethod === 'huvudregeln' ? 'Huvudregeln' : 'Förenklingsregeln'}
                            </span>
                        )}
                    </span>
                    <span className="tp-tile-value tp-tile-value-lg">
                        {formatSek(k10Result.gransbelopp)}
                    </span>
                    <span className="tp-tile-note">
                        Utdelning inom gränsbeloppet beskattas med 20 % i kapital.
                    </span>
                </div>

                {oldRules && k10Result.alternatives && (
                    <div className="ye-well">
                        Gamla reglerna (t.o.m. 2025): det bästa av förenklingsregeln
                        ({formatSek(k10Result.alternatives.forenklingsregeln)}) och
                        huvudregeln ({formatSek(k10Result.alternatives.huvudregeln)})
                        väljs automatiskt. Från 2026 gäller en gemensam regel.
                    </div>
                )}

                <div className="tp-comp-list">
                    {oldRules ? (
                        <>
                            {renderComponentRow('forenklingsregeln', comps.forenklingsregeln)}
                            {renderComponentRow('lonebaserat', comps.huvudregeln?.lonebaserat)}
                            {comps.huvudregeln?.lonebaserat?.loneuttagskrav && (
                                <div className="tp-comp-formula" style={{ paddingLeft: '0.25rem' }}>
                                    Löneuttagskrav: {formatSek(comps.huvudregeln.lonebaserat.loneuttagskrav.required)}
                                    {' — '}
                                    {comps.huvudregeln.lonebaserat.loneuttagskrav.met
                                        ? 'uppfyllt'
                                        : 'inte uppfyllt (lönebaserat utrymme = 0)'}
                                </div>
                            )}
                            {renderComponentRow('ranta', comps.huvudregeln?.ranta)}
                            {renderComponentRow('sparat', comps.sparat)}
                        </>
                    ) : (
                        <>
                            {renderComponentRow('grundbelopp', comps.grundbelopp)}
                            {renderComponentRow('lonebaserat', comps.lonebaserat)}
                            {comps.lonebaserat?.capBinding && (
                                <div className="tp-comp-formula" style={{ paddingLeft: '0.25rem' }}>
                                    Taket på 50 × högsta enskilda lön begränsar utrymmet
                                    (utan tak: {formatSek(comps.lonebaserat.raw)}).
                                </div>
                            )}
                            {renderComponentRow('ranta', comps.ranta)}
                            {renderComponentRow('sparat', comps.sparat)}
                        </>
                    )}
                </div>

                {k10Result.loneunderlagSource && (
                    <div className="ye-field-help" style={{ marginTop: '0.5rem' }}>
                        Löneunderlag: {k10Result.loneunderlagSource}.
                    </div>
                )}
                {k10Result.caveat && (
                    <div className="ye-well">{k10Result.caveat}</div>
                )}
            </div>
        );
    };

    const renderK10Card = () => (
        <div className="modern-card">
            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">
                        <Percent size={18} />
                        Utdelningsutrymme (K10)
                    </h4>
                    <p className="section-subtitle">
                        Gränsbeloppet är hur mycket utdelning du kan ta till 20 %
                        kapitalskatt (3:12-reglerna). Fyll i uppgifterna om ditt ägande
                        — resten hämtas från bokföringen.
                    </p>
                </div>
                <div className="ye-field" style={{ maxWidth: 180 }}>
                    <label htmlFor="tp-k10-year">Beskattningsår</label>
                    <select
                        id="tp-k10-year"
                        value={beskattningsar}
                        onChange={(e) => { setBeskattningsar(Number(e.target.value)); invalidateK10(); }}
                    >
                        {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="modern-card-body">
                {beskattningsar <= 2025 && (
                    <div className="ye-well" style={{ marginTop: 0, marginBottom: '1rem' }}>
                        För beskattningsår till och med 2025 gäller de gamla
                        3:12-reglerna: förenklingsregeln eller huvudregeln — den som ger
                        högst gränsbelopp väljs automatiskt.
                    </div>
                )}
                <div className="ye-form-grid">
                    <div className="ye-field">
                        <label htmlFor="tp-k10-andel">
                            Ägarandel (%)
                            <InfoIcon id="tp-help-andel" entry={helpTexts.ownership_share} />
                        </label>
                        <input
                            id="tp-k10-andel"
                            type="text"
                            inputMode="decimal"
                            value={agarandel}
                            onChange={(e) => { setAgarandel(e.target.value); invalidateK10(); }}
                        />
                    </div>
                    <div className="ye-field">
                        <label htmlFor="tp-k10-omkostnad">
                            Omkostnadsbelopp (kr)
                            <InfoIcon id="tp-help-omkostnad" entry={helpTexts.omkostnadsbelopp} />
                        </label>
                        <input
                            id="tp-k10-omkostnad"
                            type="text"
                            inputMode="decimal"
                            placeholder="t.ex. 25 000"
                            value={omkostnadsbelopp}
                            onChange={(e) => { setOmkostnadsbelopp(e.target.value); invalidateK10(); }}
                        />
                    </div>
                    <div className="ye-field">
                        <label htmlFor="tp-k10-sparat">
                            Sparat utdelningsutrymme (kr)
                            <InfoIcon id="tp-help-sparat" entry={helpTexts.sparat_utdelningsutrymme} />
                        </label>
                        <input
                            id="tp-k10-sparat"
                            type="text"
                            inputMode="decimal"
                            placeholder="0"
                            value={sparat}
                            onChange={(e) => { setSparat(e.target.value); invalidateK10(); }}
                        />
                    </div>
                    <div className="ye-field">
                        <label htmlFor="tp-k10-egenlon">
                            Egen lön förra året (kr)
                            <InfoIcon id="tp-help-egenlon" entry={OWN_SALARY_HELP} />
                        </label>
                        <input
                            id="tp-k10-egenlon"
                            type="text"
                            inputMode="decimal"
                            placeholder="0"
                            value={egenLon}
                            onChange={(e) => { setEgenLon(e.target.value); invalidateK10(); }}
                        />
                    </div>
                    <div className="ye-field">
                        <label htmlFor="tp-k10-narstaende">
                            Närståendes lön förra året (kr)
                            <InfoIcon id="tp-help-narstaende" entry={helpTexts.narstaende_cash_salary} />
                        </label>
                        <input
                            id="tp-k10-narstaende"
                            type="text"
                            inputMode="decimal"
                            placeholder="0"
                            value={narstaendeLon}
                            onChange={(e) => { setNarstaendeLon(e.target.value); invalidateK10(); }}
                        />
                        <div className="ye-field-help">Högsta enskilda person — inte familjens summa.</div>
                    </div>
                </div>

                {k10Error && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        {k10Error}
                    </div>
                )}

                {k10Result ? renderK10Result() : (
                    <div className="ye-proposal-actions">
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={handleComputeK10}
                            disabled={computingK10 || !k10InputsValid}
                        >
                            <Calculator size={16} />
                            {computingK10 ? 'Beräknar…' : 'Beräkna'}
                        </button>
                        <span className="ye-footer-hint">
                            Bara en beräkning — ingenting sparas eller lämnas in.
                        </span>
                    </div>
                )}
            </div>
        </div>
    );

    // ---- Renderers: Periodiseringsfond ------------------------------------

    const renderPfondCard = () => {
        if (!selectedFyId) return null;
        return (
            <div className="modern-card">
                <div className="modern-card-header ye-step-header">
                    <div>
                        <h4 className="section-title">
                            <PiggyBank size={18} />
                            Periodiseringsfond
                        </h4>
                        <p className="section-subtitle">
                            En avsättning skjuter upp skatten till ett senare år — det är
                            en uppskjutning, ingen besparing, och den kostar en årlig
                            schablonränta för aktiebolag.
                        </p>
                    </div>
                    <div className="ye-year-select">
                        <Select
                            theme={brandSelectTheme}
                            options={yearOptions}
                            value={yearOptions.find((o) => String(o.value) === String(selectedFyId)) || null}
                            placeholder="Välj räkenskapsår…"
                            noOptionsMessage={() => 'Inga räkenskapsår'}
                            onChange={(option) => { if (option) setSelectedFyId(option.value); }}
                        />
                    </div>
                </div>
                <div className="modern-card-body">
                    {!pfond ? (
                        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            Ingen periodiseringsfondsdata för det valda året.
                        </p>
                    ) : pfond.unavailable ? (
                        <div className="ye-well" style={{ marginTop: 0 }}>{pfond.unavailable}</div>
                    ) : (
                        <>
                            <div className="tp-tiles">
                                <div className="tp-tile">
                                    <span className="tp-tile-label">
                                        Max avsättning ({pfond.maxPct} % av överskottet)
                                    </span>
                                    <span className="tp-tile-value">{formatSek(pfond.maxAvsattning)}</span>
                                </div>
                                {pfond.schablonintakt && (
                                    <div className="tp-tile">
                                        <span className="tp-tile-label">
                                            Årlig kostnad (schablonintäkt × bolagsskatt)
                                        </span>
                                        <span className="tp-tile-value">
                                            {formatSek(pfond.schablonintakt.taxCost)}
                                        </span>
                                        <span className="tp-tile-note">
                                            {pfond.schablonintakt.formula}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {(pfond.mustReverseNow || []).length > 0 && (
                                <div className="ye-well" style={{ borderColor: '#fde68a', background: '#fffbeb', color: '#92400e' }}>
                                    Måste återföras i år (senast sjätte året efter avsättningen):
                                    <ul>
                                        {pfond.mustReverseNow.map((f) => (
                                            <li key={f.year}>
                                                Fond {f.year}: {formatSek(f.balance)} ({f.formula})
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {(pfond.notes || []).map((n, i) => (
                                <div className="ye-well" key={i}>{n}</div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        );
    };

    // Bolagsformen avgör vilka kort som är meningsfulla. Backend skickar
    // companyType i overview-svaret och utelämnar K10/EF-sektionerna för fel
    // form, men korten måste döljas här också — ett tomt K10-kort på en
    // enskild firma ser ut som ett fel.
    const isEnskildFirma = Boolean(overview?.companyType)
        && !['Aktiebolag', 'Ekonomisk förening'].includes(overview.companyType);

    // ---- Enskild firma ----------------------------------------------------
    // För en enskild näringsidkare finns varken lön till ägaren eller K10 —
    // planeringen sker i stället på NE-bilagans deklarationsposter:
    // räntefördelning (R30/R31), expansionsfond (R36/R37) och schablon-
    // avdraget för egenavgifter (R43). Siffrorna kommer från samma
    // NeComputationCore-grindar som deklarationssteget använder.
    const renderEfCard = () => {
        const rf = overview?.rantefordelning || null;
        const ef = overview?.expansionsfond || null;
        const ea = overview?.egenavgifter || null;
        if (!rf && !ef && !ea) return null;

        return (
            <div className="modern-card">
                <div className="modern-card-header ye-step-header">
                    <div>
                        <h4 className="section-title">
                            <Percent size={18} />
                            Enskild firma — deklarationsposter
                        </h4>
                        <p className="section-subtitle">
                            Räntefördelning, expansionsfond och egenavgifter är avdrag på
                            NE-bilagan, inte bokförda poster. Här ser du årets utrymme innan
                            du fattar besluten i bokslutet.
                        </p>
                    </div>
                </div>
                <div className="modern-card-body">
                    {rf && (
                        <>
                            <h5 className="tp-sub-heading">Räntefördelning</h5>
                            {rf.unavailable ? (
                                <div className="ye-well" style={{ marginTop: 0 }}>{rf.unavailable}</div>
                            ) : rf.needsInput === 'kapitalunderlag' ? (
                                <>
                                    <div className="ye-well" style={{ marginTop: 0 }}>{rf.explanation}</div>
                                    <div className="tp-field" style={{ maxWidth: 320, marginTop: '0.75rem' }}>
                                        <label htmlFor="tp-kapitalunderlag">Kapitalunderlag (kr)</label>
                                        <input
                                            id="tp-kapitalunderlag"
                                            className="form-control"
                                            inputMode="decimal"
                                            value={kapitalunderlag}
                                            onChange={(e) => setKapitalunderlag(e.target.value)}
                                            onBlur={() => {
                                                const value = parseAmount(kapitalunderlag);
                                                if (!Number.isFinite(value)) return;
                                                setKapitalunderlagOverride(value);
                                                fetchOverview({
                                                    ...(ytdOverride != null ? { ytdCashSalary: ytdOverride } : {}),
                                                    kapitalunderlag: value,
                                                });
                                            }}
                                            placeholder="t.ex. 250 000"
                                        />
                                        <span className="tp-field-help">
                                            Tillgångar minus skulder i verksamheten vid föregående års
                                            utgång — hjälpblankett SKV 2196.
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="tp-tiles">
                                        <div className="tp-tile">
                                            <span className="tp-tile-label">
                                                Positivt fördelningsbelopp ({fmtPct(rf.positivRate)} %)
                                            </span>
                                            <span className="tp-tile-value">
                                                {rf.positivTillaten ? formatSek(rf.positivtFordelningsbelopp) : '—'}
                                            </span>
                                            <span className="tp-tile-note">
                                                {rf.positivTillaten
                                                    ? 'Frivilligt. Flyttar överskott till kapitalbeskattning (30 %).'
                                                    : 'Inte aktuell med nuvarande kapitalunderlag.'}
                                            </span>
                                        </div>
                                        {rf.negativObligatorisk && (
                                            <div className="tp-tile">
                                                <span className="tp-tile-label">
                                                    Negativ räntefördelning ({fmtPct(rf.negativRate)} %)
                                                </span>
                                                <span className="tp-tile-value">
                                                    {formatSek(rf.negativtFordelningsbelopp)}
                                                </span>
                                                <span className="tp-tile-note">
                                                    Obligatorisk — kapitalunderlaget är lägre än
                                                    {' '}{formatSek(rf.negativGrans)}.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="ye-well">
                                        Kapitalunderlag: {formatSek(rf.kapitalunderlag)}. Regelverk:
                                        {' '}{rf.regelset === 'fran_2025'
                                            ? 'räkenskapsår som börjar 2025 eller senare (inget lägsta kapitalunderlag)'
                                            : 'räkenskapsår som började före 2025 (±50 000-gränserna)'}.
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {ef && !ef.unavailable && ef.needsInput === 'expansionsfond_kapitalunderlag' && (
                        <>
                            <h5 className="tp-sub-heading">Expansionsfond</h5>
                            <div className="ye-well" style={{ marginTop: 0 }}>{ef.explanation}</div>
                            <div className="tp-tiles" style={{ marginTop: '0.75rem' }}>
                                <div className="tp-field">
                                    <label htmlFor="tp-ef-underlag">
                                        Kapitalunderlag för expansionsfond (kr)
                                    </label>
                                    <input
                                        id="tp-ef-underlag"
                                        className="form-control"
                                        inputMode="decimal"
                                        value={efUnderlag}
                                        onChange={(e) => setEfUnderlag(e.target.value)}
                                        onBlur={() => {
                                            const value = parseAmount(efUnderlag);
                                            if (!Number.isFinite(value)) return;
                                            setEfUnderlagOverride(value);
                                            fetchOverview({ expansionsfondKapitalunderlag: value });
                                        }}
                                        placeholder="t.ex. 300 000"
                                    />
                                    <span className="tp-field-help">
                                        Tillgångar minus skulder vid <strong>årets slut</strong>
                                        {' '}(SKV 2196 sida 2). Inte samma tal som räntefördelningens.
                                    </span>
                                </div>
                                <div className="tp-field">
                                    <label htmlFor="tp-ef-ingaende">
                                        Redan avsatt expansionsfond (kr)
                                    </label>
                                    <input
                                        id="tp-ef-ingaende"
                                        className="form-control"
                                        inputMode="decimal"
                                        value={ingaendeEf}
                                        onChange={(e) => setIngaendeEf(e.target.value)}
                                        onBlur={() => fetchOverview({
                                            ingaendeExpansionsfond: parseOptionalAmount(ingaendeEf),
                                        })}
                                        placeholder="0"
                                    />
                                    <span className="tp-field-help">
                                        Fondens värde vid årets ingång — står på förra årets NE.
                                        Takregeln gäller fondens totala storlek.
                                    </span>
                                </div>
                            </div>
                        </>
                    )}

                    {ef && !ef.unavailable && !ef.needsInput && (
                        <>
                            <h5 className="tp-sub-heading">Expansionsfond</h5>
                            <div className="tp-tiles">
                                <div className="tp-tile">
                                    <span className="tp-tile-label">Högsta ökning i år</span>
                                    <span className="tp-tile-value">{formatSek(ef.maxOkningIAr)}</span>
                                    <span className="tp-tile-note">
                                        {ef.begransasAv === 'takregeln'
                                            ? `Begränsas av takregeln: fonden får totalt vara ${formatSek(ef.takTotal)} `
                                              + `(${fmtPct(ef.takAndelAvKapitalunderlag)} % av kapitalunderlaget), `
                                              + `varav ${formatSek(ef.ingaendeExpansionsfond)} redan är avsatt.`
                                            : `Begränsas av överskottet. Takregeln medger ${formatSek(ef.kvarTillTak)} till.`}
                                        {' '}Bindande tak räknas i bokslutet (NE R35).
                                    </span>
                                </div>
                                <div className="tp-tile">
                                    <span className="tp-tile-label">Expansionsfondsskatt</span>
                                    <span className="tp-tile-value">{fmtPct(ef.skattesats)} %</span>
                                    <span className="tp-tile-note">Återfås när fonden återförs.</span>
                                </div>
                            </div>
                            <div className="ye-well">{ef.explanation}</div>
                        </>
                    )}

                    {ea && !ea.unavailable && (
                        <>
                            <h5 className="tp-sub-heading">Egenavgifter</h5>
                            <div className="tp-tiles">
                                <div className="tp-tile">
                                    <span className="tp-tile-label">Full egenavgift</span>
                                    <span className="tp-tile-value">{fmtPct(ea.fullAvgift)} %</span>
                                    <span className="tp-tile-note">
                                        Schablonavdrag NE R43: {fmtPct(ea.schablonavdrag?.aktiv)} %
                                    </span>
                                </div>
                                <div className="tp-tile">
                                    <span className="tp-tile-label">Reducerad avgift</span>
                                    <span className="tp-tile-value">{fmtPct(ea.reduceradAvgift)} %</span>
                                    <span className="tp-tile-note">
                                        Schablonavdrag: {fmtPct(ea.schablonavdrag?.reducerad)} %
                                    </span>
                                </div>
                                <div className="tp-tile">
                                    <span className="tp-tile-label">Passiv verksamhet (SLF)</span>
                                    <span className="tp-tile-value">{fmtPct(ea.slfPassiv)} %</span>
                                    <span className="tp-tile-note">
                                        Schablonavdrag: {fmtPct(ea.schablonavdrag?.passiv)} %
                                    </span>
                                </div>
                            </div>
                            <div className="ye-well">{ea.explanation}</div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ---- Layout -----------------------------------------------------------

    const disclaimer = k10Result?.disclaimer || overview?.disclaimer || null;

    return (
        <>
            <style>{`
                .tp-page-container {
                    max-width: 1100px;
                    margin: 0 auto;
                    padding: 1.5rem 1rem;
                }
                .tp-page-container .modern-card + .modern-card { margin-top: 1rem; }
                .tp-info-btn {
                    background: none;
                    border: none;
                    padding: 0 0 0 4px;
                    color: #9ca3af;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    vertical-align: -2px;
                }
                .tp-info-btn:hover, .tp-info-btn:focus-visible { color: var(--primary); outline: none; }
                .tp-tiles {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-top: 1rem;
                }
                .tp-tile {
                    flex: 1 1 240px;
                    max-width: 380px;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 0.875rem 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }
                .tp-tile-primary {
                    background: var(--rgba-primary-1);
                    border-color: var(--rgba-primary-2);
                    max-width: 420px;
                }
                .tp-tile-label {
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #6b7280;
                    display: inline-flex;
                    align-items: center;
                    flex-wrap: wrap;
                }
                .tp-tile-value {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: var(--title, #111827);
                    font-variant-numeric: tabular-nums;
                }
                .tp-tile-value-lg { font-size: 1.5rem; }
                .tp-tile-note { font-size: 0.75rem; color: #6b7280; line-height: 1.5; }
                .tp-bar-wrap { margin: 1.75rem 0 0.5rem; }
                .tp-bar {
                    position: relative;
                    height: 12px;
                    border-radius: 6px;
                    background: #f3f4f6;
                    border: 1px solid #e5e7eb;
                }
                .tp-bar-fill {
                    height: 100%;
                    border-radius: 6px;
                    background: #22c55e;
                    transition: width 0.3s ease;
                }
                .tp-bar-fill-over { background: #f59e0b; }
                .tp-bar-marker {
                    position: absolute;
                    top: -6px;
                    bottom: -6px;
                    width: 2px;
                    background: #6b7280;
                }
                .tp-bar-marker-label {
                    position: absolute;
                    top: -1.35rem;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 0.7rem;
                    color: #6b7280;
                    white-space: nowrap;
                }
                .tp-bar-marker-label-low { top: auto; bottom: -1.35rem; }
                .tp-bar-caption {
                    margin-top: 2rem;
                    font-size: 0.8125rem;
                    color: #4b5563;
                }
                .tp-k10-result { margin-top: 1.25rem; }
                .tp-comp-list {
                    margin-top: 1rem;
                    max-width: 640px;
                    display: flex;
                    flex-direction: column;
                    gap: 0.6rem;
                }
                .tp-comp-row {
                    border-bottom: 1px solid #f3f4f6;
                    padding-bottom: 0.5rem;
                }
                .tp-comp-main {
                    display: flex;
                    justify-content: space-between;
                    gap: 1rem;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: var(--title, #374151);
                }
                .tp-comp-main .ye-num {
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                }
                .tp-comp-formula {
                    font-size: 0.75rem;
                    color: #9ca3af;
                    margin-top: 0.15rem;
                    line-height: 1.5;
                }
                .tp-disclaimer {
                    margin-top: 1.25rem;
                    font-size: 0.75rem;
                    color: #9ca3af;
                    line-height: 1.6;
                    max-width: 76ch;
                }
                /* Shared ye-* form chrome (same rules as the year-end wizard). */
                .ye-step-header { flex-wrap: wrap; gap: 0.75rem; align-items: flex-start; }
                .ye-step-header .section-subtitle { max-width: 62ch; }
                .ye-form-grid { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; }
                .ye-field { flex: 1 1 180px; max-width: 260px; }
                .ye-field label {
                    display: block;
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #4b5563;
                    margin-bottom: 0.3rem;
                }
                .ye-field input, .ye-field select {
                    width: 100%;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.45rem 0.75rem;
                    font-size: 0.875rem;
                    background: #fff;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .ye-field input:focus, .ye-field select:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                }
                .ye-field-help { font-size: 0.75rem; color: #9ca3af; margin-top: 0.25rem; }
                .ye-proposal-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                    margin-top: 1rem;
                }
                .ye-footer-hint { color: #6b7280; font-size: 0.8125rem; }
                .ye-well {
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.625rem 0.875rem;
                    font-size: 0.8125rem;
                    color: #4b5563;
                    margin-top: 0.75rem;
                }
                .ye-well ul { margin: 0.25rem 0 0 0; padding-left: 1.1rem; }
                .ye-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.375rem;
                    padding: 0.25rem 0.625rem;
                    border-radius: 16px;
                    border: 1px solid transparent;
                    font-size: 0.75rem;
                    font-weight: 600;
                    line-height: 1.4;
                    white-space: nowrap;
                }
                .ye-badge-primary {
                    background: var(--rgba-primary-1);
                    color: var(--primary);
                    border-color: var(--rgba-primary-2);
                }
                /* EF-kortets underrubriker och fält */
                .tp-sub-heading {
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    color: #6b7280;
                    margin: 1.5rem 0 0.75rem;
                }
                .tp-sub-heading:first-child { margin-top: 0; }
                .tp-field { display: flex; flex-direction: column; }
                .tp-field label {
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.25rem;
                }
                .tp-field-help {
                    font-size: 0.75rem;
                    color: #9ca3af;
                    margin-top: 0.25rem;
                    line-height: 1.45;
                }
                .ye-year-select { min-width: 300px; }
                @media (max-width: 768px) {
                    .ye-year-select { min-width: 0; width: 100%; }
                }
            `}</style>

            <div className="tp-page-container">
                <div className="modern-card">
                    <div className="modern-card-header ye-step-header">
                        <div>
                            <h3 className="section-title">
                                <Percent size={20} />
                                Skatteplanering
                            </h3>
                            <p className="section-subtitle">
                                Vad händer om? Räkna på lön, utdelningsutrymme och
                                periodiseringsfond — utifrån dina bokförda siffror.
                                Ingenting bokförs eller lämnas in härifrån.
                            </p>
                        </div>
                    </div>
                </div>

                {loadingOverview && !overview ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: 220 }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Laddar…</span>
                        </div>
                    </div>
                ) : forbidden ? (
                    <div className="modern-card">
                        <div className="modern-card-body">
                            <p className="text-muted mb-0">
                                Skatteplaneringen kan bara användas av företagets
                                administratörer. Be en administratör om hjälp, eller om att
                                ge dig rätt behörighet.
                            </p>
                        </div>
                    </div>
                ) : overviewError ? (
                    <div className="modern-card">
                        <div className="modern-card-body">
                            <div className="alert alert-danger mb-2" style={{ fontSize: '0.875rem' }}>
                                {overviewError}
                            </div>
                            <button
                                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                                onClick={() => fetchOverview(ytdOverride != null
                                    ? { ytdCashSalary: ytdOverride } : {})}
                            >
                                <RefreshCw size={14} />
                                Försök igen
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* En enskild näringsidkare tar inte ut lön och har ingen
                            K10 — de korten skulle bara vara vilseledande. */}
                        {isEnskildFirma ? (
                            <>
                                {renderEfCard()}
                                {renderPfondCard()}
                            </>
                        ) : (
                            <>
                                {renderSalaryCard()}
                                {renderK10Card()}
                                {renderPfondCard()}
                            </>
                        )}
                        {disclaimer && <p className="tp-disclaimer">{disclaimer}</p>}
                    </>
                )}
            </div>
        </>
    );
}

export default TaxPlanningPage;
